import {action, observable, computed, observe, set, has} from 'mobx';
import urls from 'urls';
import {evite} from 'evite';
import {failedFetchModal} from '~/modal/_ajax_error_handler';
import {getURLParameter, stringifyParams} from '~/common/utils';
import {
  PREMIUM_GUEST_LIMIT,
  FREE_GUEST_LIMIT,
  UNLIMITED_GUESTS_OFFERS_LIST_NAME,
  ALTERNATIVE_INVITATION_OFFER_LISTS,
} from '~/create/constants';
import {isSubscriptionOffer} from '~/common/components/Stripe/utils';
import {INVITATION_OFFER_LIST} from '../constants';
import {creationStore} from '~/create/stores/index';

export class PaymentStore {
  @observable accessor loading = false;

  @observable accessor updating = false;

  @observable accessor calc = {
    hasError: false,
    errorMessage: '',

    needsMoneyToSend: true,
    needsUserPaymentInfo: true,
    amountToBeCharged: '$0.00',
    centsToBeCharged: 0,
    numGuestsBeingAdded: 0,
    profileCreditsAvailable: 0,
    profileCreditsToUse: 0,
    eventCreditsAvailable: 0,
    totalCreditsAvailable: 0,
    sendCreditsToRemain: 0,
    numSendsToBePurchased: 0,
    isSendsOfferNeeded: false,
    selectedOffers: {},
    selectedOfferDescriptions: [],

    promoCreditsAvailable: 0,
    promocode: null,
    promoDescription: null,
    promoError: '',
    promoDiscountCents: 0,
    promoDiscountStr: '$0.00',
    promoIsMonetary: false,
    promoType: null,

    redemptionCode: null,

    userHasActivePremiumPass: false,
    userOwnsActivePremiumPass: true,
    premiumPassExpiration: null,
    premiumPassExpirationStr: '',
    subscriptionPlanName: '',
    subscriptionFreeTrialAvailable: false,
    subscriptionFreeTrialNumDays: 0,
    subscriptionFirstPeriodChargeCents: 0,
    subscriptionFirstPeriodChargeStr: '',
    subscriptionPlanOfferName: '',

    cardOnFile: false,
    cardLast4: '',

    offerListName: '',

    freeSendsOfferUsed: false,
    previouslyPurchasedOffer: false,
    isBeingDowngraded: false,
  };

  purchase = {};

  event = null;

  guestStore = null;

  currentlyDowngraded = false;

  @observable accessor purchaseTransactionId = null;

  @observable accessor sendTransactionId = null;

  constructor(
    initialCalcData = null,
    loading = false,
    event = null,
    guestStore = null,
    standalonePayment = false
  ) {
    this.sendTransactionId = null;
    this.transactionData = null;

    if (initialCalcData) {
      Object.assign(this.calc, initialCalcData);
    } else if (window.paymentCalcSummary) {
      Object.assign(this.calc, window.paymentCalcSummary);
    }

    this.loading = loading;
    this.event = event;
    this.guestStore = guestStore;
    this.isStandalonePayment = standalonePayment;

    if (this.guestStore) {
      observe(this.guestStore.guests, (change) => {
        if (!change || change.name !== 'totalRecords') return;
        if (event.is_host_viewing) {
          const queryOptions = {
            // if event has already been sent, select the "minimum" required
            // sends offer when draft guests change
            selectMinimumSendsOffer: this.hasEvent && Boolean(this.event.send_count),
          };
          this.update(queryOptions);
        }
      });
    }
  }

  @computed
  get getPricingTierOffers() {
    const pricingTierOffers = {};
    this.calc.availableOffers.map((offerObj) => {
      pricingTierOffers[offerObj.offerKey] = offerObj;
    });
    return pricingTierOffers;
  }

  getAvailableSendsOffers = () =>
    this._getOffersOfProductTypes(['premium_invitation_sends', 'individual_greeting_card']);

  getAvailablePremiumPassSubscriptionOffers = () =>
    this._getOffersOfProductTypes(['premium_pass_subscription']).reverse();

  getAvailablePartyPassSubscriptionOffers = () =>
    this._getOffersOfProductTypes(['premium_pass_subscription_restricted']);

  getAvailableGreetingCardSubscriptionOffers = () =>
    this._getOffersOfProductTypes(['greeting_card_subscription']);

  getSendsOfferPriceRangeStr = () => {
    // as seen in the "Upgrade to Premium" dialog (free upsell)
    const sendsOffers = this.getAvailableSendsOffers();
    if (sendsOffers.length === 0) return 'a small fee'; // :man-shrugging: this shouldn't happen, soooo...

    if (sendsOffers.length === 1) return sendsOffers[0].displayPrice;

    // sends offers should be in increasing price order, so first should be cheapest
    // and last should be most expensive
    const cheapest = sendsOffers[0];
    const mostExpensive = sendsOffers[sendsOffers.length - 1];
    return `${cheapest.displayPrice} - ${mostExpensive.displayPrice}`;
  };

  _getOffersOfProductType = (productType) => {
    const offers = [];
    this.calc.availableOffers.forEach((offerObj) => {
      if (offerObj.productType === productType) offers.push(offerObj);
    });
    return offers;
  };

  _getOffersOfProductTypes = (productTypes) => {
    const offers = [];
    this.calc.availableOffers.forEach((offerObj) => {
      if (productTypes.includes(offerObj.productType)) offers.push(offerObj);
    });
    return offers;
  };

  // helper to update state
  _applyUpdate = (data) => {
    for (const [k, v] of Object.entries(data.calc)) {
      set(this.calc, k, v);
    }

    if (this.hasEvent) {
      // we need to update the selected offers object on event or the next
      // event update will overwrite the offer selection
      this.event.options.offers = {...this.calc.selectedOffers};
    }
  };

  /**
   * Fetch payment details from the server
   */
  @action
  update = async (queryOptions = {}) => {
    this._setUpdating(true);
    const errorDefault =
      'Unknown error loading checkout details. Please check your network ' +
      'connection and try again.';

    const queryParams = {};
    if (this.hasEvent) queryParams.event_id = this.eventId;
    if (this.calc.offerListName) queryParams.offer_list_name = this.calc.offerListName;

    // check options
    if (Boolean(queryOptions.selectMinimumSendsOffer) && this.hasEvent)
      queryParams.select_minimum_sends_offer = 'true';
    if (queryOptions.autoApplyRedemptionCode) queryParams.auto_apply_redemption_code = 'true';

    let url = urls.get('ajax_payment_calc');
    if (Object.keys(queryParams).length > 0) url += `?${stringifyParams(queryParams)}`;

    try {
      const response = await evite.fetch(url, {
        method: 'get',
        credentials: 'include',
        cache: 'no-cache',
        headers: {
          'X-CSRFToken': evite.cookie('csrftoken'),
          Accept: 'application/json',
        },
      });
      let data = await response.json();

      if (!response.ok) {
        const error = data.message || errorDefault;
        failedFetchModal(response, 'Error', error);
        this._setUpdating(false);
        return;
      }

      // not sure how @action meshes with an async function.
      // probably not a bag of awesome.
      action(() => {
        this.loading = false;
        this._setUpdating(false);
        if (!data.calc) {
          // todo when this is deployed the next sprint can change the endpoint
          data = {calc: data};
        }
        this._applyUpdate(data);
      })();
    } catch (e) {
      evite.error('Failed to update', e);
      failedFetchModal(null, 'Error', errorDefault);
      this._setUpdating(false);
    }
  };

  applyPromotion = async (promocode) => {
    this._setUpdating(true);
    const errorDefault =
      'Unknown error while applying the promotion. Please check your network ' +
      'connection and try again.';

    try {
      const body = new FormData();
      body.append('promocode', promocode);
      if (this.hasEvent) body.append('event_id', this.event.id);
      if (this.calc.offerListName) body.append('offer_list_name', this.calc.offerListName);

      const url = urls.get('ajax_apply_promotion');

      const response = await evite.fetch(url, {
        method: 'post',
        body,
        credentials: 'include',
        cache: 'no-cache',
        headers: {
          'X-CSRFToken': evite.cookie('csrftoken'),
          Accept: 'application/json',
        },
      });
      const data = await response.json();

      if (!response.ok) {
        const error = data.message || errorDefault;
        failedFetchModal(response, 'Error', error);
        this._setUpdating(false);
        return;
      }

      action(() => {
        console.log('DATA', data);
        this._setUpdating(false);
        this._applyUpdate(data);
      })();
    } catch (e) {
      evite.error('Failed to update', e);
      failedFetchModal(null, 'Error', errorDefault);
      this._setUpdating(false);
    }
  };

  removePromocode = async (e) => {
    if (e) e.preventDefault();

    this._setUpdating(true);
    const errorDefault =
      'Unknown error while removing the promotion. Please check your network ' +
      'connection and try again.';

    try {
      let url = urls.get('ajax_apply_promotion');

      const queryParams = {};
      if (this.hasEvent) queryParams.event_id = this.event.id;
      if (this.calc.offerListName) queryParams.offer_list_name = this.calc.offerListName;

      if (Object.keys(queryParams).length > 0) url += `?${stringifyParams(queryParams)}`;

      const response = await evite.fetch(url, {
        method: 'delete',
        credentials: 'include',
        cache: 'no-cache',
        headers: {
          'X-CSRFToken': evite.cookie('csrftoken'),
          Accept: 'application/json',
        },
      });
      const data = await response.json();

      if (!response.ok) {
        const error = data.message || errorDefault;
        failedFetchModal(response, 'Error', error);
        this._setUpdating(false);
        return;
      }

      action(() => {
        this._setUpdating(false);
        this._applyUpdate(data);
      })();
    } catch (e) {
      evite.error('Failed to update', e);
      failedFetchModal(null, 'Error', errorDefault);
      this._setUpdating(false);
    }
  };

  // This isn't getting called right now, and probably isn't needed.
  // Instead, we use the observed transaction properties.
  fetchTransaction = async () => {
    const errorDefault = 'Unknown error while retrieving transaction history';

    try {
      // TODO - there are actually two possible transactions - look at
      // the use of this.purchaseTransactionId and this.sendTransactionId
      // - probably use one (or both) of those instead of the
      // getURLParameter('p') below, etc. Which transaction are we
      // caring about here, and why? For now we'll start with the purchase
      // transaction ("p") and if that doesn't exist we'll go with the
      // sends transaction ("s").

      // We're trying to keep the transactionId and sendTransactionId observed,
      // so don't see why we'd need to scrape the url to get them.

      let transactionId = getURLParameter('p');
      if (!transactionId) transactionId = getURLParameter('s');

      if (!transactionId) throw new Error('Invalid transaction');

      // TODO is this (below) needed?
      if (this.purchaseTransactionId === transactionId || this.sendTransactionId === transactionId)
        return;

      const url = `${urls.get('payments_get_transaction')}?${a}`;
      stringifyParams({
        event_id: this.event.id,
        transaction_id: transactionId,
      });

      const response = await evite.fetch(url, {
        method: 'get',
        credentials: 'include',
        cache: 'no-cache',
        headers: {
          'X-CSRFToken': evite.cookie('csrftoken'),
          Accept: 'application/json',
        },
      });
      const transactionData = await response.json();

      if (!response.ok) {
        const error = transactionData.message || errorDefault;
        failedFetchModal(response, 'Error', error);
        return;
      }

      this.transactionData = transactionData;
    } catch (e) {
      evite.error('Failed to update', e);
      failedFetchModal(null, 'Error', errorDefault);
    }
  };

  updateSelectedOffers = async (autoApplyRedemptionCode = false) => {
    if (this.event) {
      // always make sure the event options is up to date
      this.event.options.offers = {...this.calc.selectedOffers};
    }
    if (!this.eventId && !this.isStandalonePayment) {
      // we can't update offers if we haven't created the event yet
      return;
    }

    // updates event offers on the datastore with the currently selected offers
    this._setUpdating(true);
    const errorDefault =
      'Unknown error while updating your selection. Please check your network ' +
      'connection and try again.';

    try {
      const body = new FormData();
      body.append('offer_data', JSON.stringify(this.calc.selectedOffers));
      body.append('offer_list_name', this.calc.offerListName);
      body.append('num_guests_being_added', JSON.stringify(this.numGuestsAdded));

      if (this.hasEvent) body.append('event_id', this.event.id);

      const url = `${urls.get('ajax_update_offers')}${
        autoApplyRedemptionCode ? '?auto_apply_redemption_code=true' : ''
      }`;
      const response = await evite.fetch(url, {
        method: 'post',
        body,
        credentials: 'include',
        cache: 'no-cache',
        headers: {
          'X-CSRFToken': evite.cookie('csrftoken'),
          Accept: 'application/json',
        },
      });
      const data = await response.json();

      if (!response.ok) {
        const error = data.message || errorDefault;
        failedFetchModal(response, 'Error', error);
        this._setUpdating(false);
        return;
      }

      action(() => {
        this._applyUpdate(data);
        this._setUpdating(false);
      })();
    } catch (e) {
      evite.error('Failed to update', e);
      failedFetchModal(null, 'Error', errorDefault);
      this._setUpdating(false);
    }
  };

  @computed
  get hasEvent() {
    if (!this.event) return false;

    return this.event.isValidEvent();
  }

  @computed
  get eventId() {
    if (!this.hasEvent) return null;
    return this.event.id;
  }

  @computed
  get hasPaymentCalcError() {
    return this.calc.hasError;
  }

  @computed
  get paymentCalcErrorMessage() {
    return this.calc.errorMessage;
  }

  @computed
  get needsMoneyToSend() {
    return this.calc.needsMoneyToSend;
  }

  @computed
  get isCurrentlyDowngraded() {
    return this.currentlyDowngraded;
  }

  @computed
  get offerToBeCharged() {
    if (this.isSubscriptionFreeTrialSelected) return this.premiumPassOfferData;
    if (this.calc.userHasActivePremiumPass || !this.calc.needsMoneyToSend) return null;
    if (this.premiumPassOfferSelected) return this.premiumPassOfferData;
    if (this.partyPassOfferSelected) return this.partyPassOfferData;
    if (this.sendsOfferNeeded) {
      if (this.selectedSendsOffer) return this.selectedSendsOffer;
    }
    return null;
  }

  @computed
  get isSubscriptionSelected() {
    return isSubscriptionOffer(this.offerToBePurchased);
  }

  @computed
  get isSubscriptionFreeTrialSelected() {
    return this.calc.subscriptionFreeTrialAvailable && this.isSubscriptionSelected;
  }

  @computed
  get keyForOfferToBeCharged() {
    const offerData = this.offerToBeCharged;
    if (offerData) return offerData.offerKey;
    return null;
  }

  @computed
  get amountToBeChargedStr() {
    return this.calc.centsToBeCharged === 0 ? 'FREE' : this.calc.amountToBeCharged;
  }

  @computed
  get amountToBeCharged() {
    return this.calc.amountToBeCharged;
  }

  @computed
  get isSingleItemPurchase() {
    let numOffersAvailable = 0;
    if (this.sendsOffersAvailable) numOffersAvailable += this.getAvailableSendsOffers().length;
    if (this.premiumPassOfferAvailable) numOffersAvailable += 1;
    return numOffersAvailable === 1;
  }

  @computed
  get sendsOffersAvailable() {
    return this.getAvailableSendsOffers().length > 0;
  }

  /**
   *  If the available credits (from the event, from the user's profile
   * and from a promo code) cover the number of guests added, then the
   * sends offer isn't needed. This method returns whether a sends offer
   * is needed or not.
   */
  @computed
  get sendsOfferNeeded() {
    if (this.calc.userHasActivePremiumPass || this.premiumPassOfferSelected)
      // if user already has an active premium pass or if they're buying
      // one now, no sends offer is needed
      return false;

    const sendCredits = this.calc.totalCreditsAvailable;
    if (this.numGuestsAdded < 1 && !sendCredits) {
      // no guests being added now - a sends offer is still needed
      // since there aren't credits available
      return true;
    }
    return this.numGuestsAdded > sendCredits;
  }

  @computed
  get premiumPassOfferData() {
    const subOffers = this.getAvailablePremiumPassSubscriptionOffers();
    if (!subOffers || subOffers.length < 0) return null;
    return subOffers[0];
  }

  // TODO should refactor - only difference is premium pass reverses the string in getAvailablePremiumPassSubscriptionOffers()
  @computed
  get partyPassOfferData() {
    const subOffers = this.getAvailablePartyPassSubscriptionOffers();
    if (!subOffers || subOffers.length < 0) return null;
    const selectedOfferKey = Object.keys(this.calc.selectedOffers)[0];

    return subOffers.filter((availableOffer) => availableOffer.offerKey === selectedOfferKey)[0];
  }

  @computed
  get premiumPassOfferAvailable() {
    return Boolean(this.premiumPassOfferData);
  }

  @computed
  get premiumPassOfferSelected() {
    const subOffers = this.getAvailablePremiumPassSubscriptionOffers();
    if (!subOffers) {
      return false;
    }
    let isAnySelected = false;
    subOffers.forEach((offer) => {
      if (this.calc.selectedOffers[offer.offerKey] >= 1) isAnySelected = true;
    });
    return isAnySelected;
  }

  // TODO refactor - don't need this lookup with only one offer
  @computed
  get partyPassOfferSelected() {
    const subOffers = this.getAvailablePartyPassSubscriptionOffers();
    if (!subOffers) {
      return false;
    }
    let isAnySelected = false;
    subOffers.forEach((offer) => {
      if (this.calc.selectedOffers[offer.offerKey] >= 1) isAnySelected = true;
    });
    return isAnySelected;
  }

  @computed
  get selectedSendsOffer() {
    const pricingTierOffers = this.getPricingTierOffers;
    for (const key of Object.keys(this.calc.selectedOffers)) {
      if (pricingTierOffers[key] && this.calc.selectedOffers[key] !== 0) {
        return pricingTierOffers[key];
      }
    }
    return null;
  }

  isSelectedSendsOfferFree() {
    const offer = this.selectedSendsOffer;
    if (!offer) return false;
    return offer.amount === 0;
  }

  @computed
  get allowFreeSend() {
    return !(this.calc.freeSendsOfferUsed || this.calc.previouslyPurchasedOffer);
  }

  @computed
  get totalGuests() {
    return this.numGuestsAdded + this.calc.numGuestsAlreadySent;
  }

  @computed
  get allowedSendsOfferKeys() {
    if (!this.isStandalonePayment) {
      // Allowed sends offer should be empty if the total of sent guests and draft guest is > 750
      if (this.totalGuests > FREE_GUEST_LIMIT) {
        return [];
      }
    }

    return this.calc.allowedSendsOfferKeys;
  }

  @computed
  get allAllowedSendsOfferKeys() {
    const availableSubscriptionOffers = this.getAvailablePremiumPassSubscriptionOffers().map(
      (offerObj) => offerObj.offerKey
    );
    return this.allowedSendsOfferKeys.concat(availableSubscriptionOffers);
  }

  @computed
  get speculativeNumSendsNeeded() {
    let numSendsNeeded = this.numSendsNeededToPurchase;

    // so, this function is speculative. for free, no sends are needed unless an offer is selected.
    // this method should return the allowed offers *should* an offer be selected.
    // so we need to adjust for the free upsell in the case of previously sent guests
    if (!this.calc.previouslyPurchasedOffer && !this.selectedSendsOffer) {
      numSendsNeeded += this.calc.numGuestsAlreadySent;
    }
    return numSendsNeeded;
  }

  @computed
  get numGuestsAdded() {
    if (!this.hasEvent) return 0;

    if (this.guestStore.isInitialized) return this.guestStore.guests.totalRecords;

    return this.calc.numGuestsBeingAdded;
  }

  @computed
  get numSendsNeededToPurchase() {
    return this.calc.numSendsToBePurchased;
  }

  @computed
  get numSendsRemaining() {
    return this.calc.sendCreditsToRemain;
  }

  @computed
  get canSendsOfferBeChanged() {
    return this.allowedSendsOfferKeys.length > 1;
  }

  @computed
  get usingCardOnFile() {
    return this.calc.cardOnFile && !!this.calc.cardLast4;
  }

  /**
   * just use the boolean class property
   * @deprecated
   * @returns {boolean}
   */
  @computed
  get isUpdating() {
    return this.updating;
  }

  @computed
  get activePremiumPass() {
    return this.calc.userHasActivePremiumPass;
  }

  @computed
  get premiumPassExpiresStr() {
    return this.calc.premiumPassExpirationStr;
  }

  @computed
  get offerToBePurchased() {
    // This function is to support EVT-15393. If there's a
    // subscription selected, that's what the user will end
    // up paying for (since that will only be deliberately
    // chosen by the user). Otherwise just return
    // this.minimumPricingTierOffer, which is "the minimum
    // purchase to support the sends you need."
    if (this.premiumPassOfferSelected) {
      return this.getAvailablePremiumPassSubscriptionOffers()[0];
    }
    return this.minimumPricingTierOffer;
  }

  @computed
  get minimumPricingTierOffer() {
    // returns the cheapest offer (sends or subscription) to cover the sends needed
    if (!this.isStandalonePayment) {
      // Allowed sends offer should be empty if the total of sent guests and draft guest is > 750
      if (this.totalGuests > FREE_GUEST_LIMIT) {
        if (this.totalGuests <= PREMIUM_GUEST_LIMIT) {
          let proSubscription = null;
          this.getAvailablePremiumPassSubscriptionOffers().forEach((offer) => {
            if (offer.productType === 'premium_pass_subscription') {
              proSubscription = offer;
            }
          });
          return proSubscription;
        }
        return null;
      }
    }

    const numSendsNeeded = this.speculativeNumSendsNeeded;

    const sendsOffers = this.getAvailableSendsOffers();
    for (let idx = 0; idx < sendsOffers.length; idx++) {
      const offer = sendsOffers[idx];
      if (!offer.hasOwnProperty('virtualQuantity')) continue;
      const offerNumSendsProvided = offer.virtualQuantity;
      // special-case check: a free, single-send offer can only be used once
      if (!this.allowFreeSend && offer.amount === 0 && offerNumSendsProvided === 1) continue;
      if (offerNumSendsProvided >= numSendsNeeded) {
        return offer;
      }
    }
    return null;
  }

  @computed
  get isUnlimitedGuestsOfferList() {
    return this.calc.offerListName === UNLIMITED_GUESTS_OFFERS_LIST_NAME;
  }

  @computed
  get hasInvitationBeenSent() {
    return this.hasEvent && this.event.hasBeenSent();
  }

  @computed
  get buyActionText() {
    let whenStr = 'Now';
    if (this.hasEvent) {
      // see if we might be sending later...
      const sendImmediately = !this.event.send_on;
      const alreadySent = this.event.send_count > 0;
      if (!sendImmediately && !alreadySent) {
        // not sending now, and hasn't been sent yet
        whenStr = 'Later';
      }
    }
    const isSubscription = this.isSubscriptionSelected;
    const subscriptionAction = this.isSubscriptionFreeTrialSelected
      ? 'Activate Free Trial'
      : 'Sign Up';
    const actionText = isSubscription ? subscriptionAction : 'Buy';
    if (this.isStandalonePayment) {
      return isSubscription ? actionText : `${actionText} ${whenStr}`;
    }

    if (this.numGuestsAdded > 0) {
      return `${this.calc.needsMoneyToSend ? `${actionText} & ` : ''}Send ${whenStr}`;
    }

    return `${actionText} ${whenStr}`;
  }

  clearSelectedOffers = () => {
    for (const key of Object.keys(this.calc.selectedOffers || {})) {
      this.calc.selectedOffers[key] = 0;
    }
  };

  /**
   *  This switches back the offer list name to the default for free
   *  invitations, INVITATION_OFFER_LIST, after a downgrade from one of
   *  the non-default or alternative offers list for the free events'
   *  creation flow
   */
  _fixOfferListName = () => {
    if (ALTERNATIVE_INVITATION_OFFER_LISTS.includes(this.calc.offerListName)) {
      this.calc.offerListName = INVITATION_OFFER_LIST;
    }
  };

  @action
  removeSelectedOffers = () => {
    this.clearSelectedOffers();
    return this.updateSelectedOffers();
  };

  @action
  downgrade = async () => {
    this.isBeingDowngraded = true;
    await this.removeSelectedOffers();
    this._fixOfferListName();
    const errorDefault =
      'Unknown error loading checkout details. Please check your network ' +
      'connection and try again.';
    if (!this.eventId && !this.isStandalonePayment) {
      // we can't update offers if we haven't created the event yet
      return;
    }

    const body = new FormData();
    if (this.hasEvent) body.append('event_id', this.event.id);

    const url = urls.get('ajax_downgrade');
    try {
      const response = await evite.fetch(url, {
        method: 'post',
        body,
        credentials: 'include',
        cache: 'no-cache',
        headers: {
          'X-CSRFToken': evite.cookie('csrftoken'),
          Accept: 'application/json',
        },
      });
      if (!response.ok) {
        const data = await response.json();
        const error = data.message || errorDefault;
        failedFetchModal(response, 'Error', error);
        this._setUpdating(false);
        return;
      }
      const data = await response.json();
      const {options} = data.event;
      this.event.options = {...options};
      this.clearSelectedOffers();
      this.update();

      this.currentlyDowngraded = true;
    } catch (e) {
      evite.error('Failed to update', e);
      failedFetchModal(null, 'Error', errorDefault);
      this._setUpdating(false);
    }
  };

  _selectOnlyThisOffer = (offerKey) => {
    this.clearSelectedOffers();
    this.calc.selectedOffers[offerKey] = 1;
  };

  _setUpdating = (updating) => {
    this.updating = updating;
  };

  getSendsOfferToSelect = (allowedOfferKeys, alsoSelectIt = true) => {
    if (this.selectedSendsOffer && allowedOfferKeys.includes(this.selectedSendsOffer.offerKey)) {
      // currently selected offer is fine, so no change needed - return its key
      return this.selectedSendsOffer.offerKey;
    }
    const newSelectedOfferKey = allowedOfferKeys[0]; // cheapest allowed
    if (alsoSelectIt) this.selectPricingTier(newSelectedOfferKey);

    return newSelectedOfferKey;
  };

  isOfferSelected = (offerKey) => Object.keys(this.calc.selectedOffers).includes(offerKey);

  @action
  selectPricingTier = (offerKey, updateOnServer = false) => {
    const wasFree = this.isSelectedSendsOfferFree();
    this._selectOnlyThisOffer(offerKey);
    if (updateOnServer) {
      const isFree = this.isSelectedSendsOfferFree();
      // if a free sends offer was previously selected, but now a not-free
      // (so requires $) one is, have the backend possibly apply a redemption
      // code if the user has one available
      const autoApplyRedemptionCode = wasFree && !isFree;
      return this.updateSelectedOffers(autoApplyRedemptionCode);
    }
  };

  @action
  selectProSubscription = () => {
    let proSubscription = null;
    this.getAvailablePremiumPassSubscriptionOffers().forEach((offer) => {
      if (offer.productType === 'premium_pass_subscription') {
        proSubscription = offer;
      }
    });
    if (!proSubscription) return;
    const currSendsOffer = this.selectedSendsOffer;
    if (currSendsOffer) this.lastSelectedSendsOfferKey = currSendsOffer.offerKey;
    this._selectOnlyThisOffer(proSubscription.offerKey);
    this.updateSelectedOffers();
    return proSubscription;
  };

  @action
  selectPremiumPass = (term = 366) => {
    const subOffers = this.getAvailablePremiumPassSubscriptionOffers();
    if (!subOffers.length) return; // no-can-do

    const currSendsOffer = this.selectedSendsOffer;
    if (currSendsOffer) this.lastSelectedSendsOfferKey = currSendsOffer.offerKey;

    for (const offer of subOffers) {
      if (offer.virtualQuantity === term) {
        this._selectOnlyThisOffer(offer.offerKey);
        this.updateSelectedOffers(); // updates selection on the server
        return;
      }
    }
  };

  @action
  deselectPremiumPass = () => {
    this.clearSelectedOffers();
    const {allowedSendsOfferKeys} = this;
    if (
      this.lastSelectedSendsOfferKey &&
      allowedSendsOfferKeys.includes(this.lastSelectedSendsOfferKey)
    ) {
      this._selectOnlyThisOffer(this.lastSelectedSendsOfferKey);
    } else {
      this.getSendsOfferToSelect(allowedSendsOfferKeys);
    }
    return this.updateSelectedOffers(); // updates selection on the server
  };

  @action
  togglePremiumPassRadio = (e) => {
    if (e.target.value === 'true') this.selectPremiumPass();
    else this.deselectPremiumPass();
  };

  @action
  userWantsToChangeCardInfo = () => {
    this.calc.cardOnFile = false;
    this.calc.cardLast4 = '';
  };

  @action
  selectSmallerAvailableOffer = (updateOnServer = true) => {
    const smallerOffer = this.getSmallerAvailableOffer();
    if (!smallerOffer || this.isOfferSelected(smallerOffer.offerKey)) return {};

    this._selectOnlyThisOffer(smallerOffer.offerKey);

    if (updateOnServer) this.updateSelectedOffers();

    return smallerOffer;
  };

  getSmallerAvailableOffer = () => {
    const {allowedSendsOfferKeys} = this;
    const availableSendsOffers = this.getAvailableSendsOffers();

    // check for the special case of a free invitation that's not an upgrade - need this for messaging about what
    // it would cost to upgrade to ad-free/premium features
    if (
      this.hasEvent &&
      this.event.origin !== 13 && // not a premium invitation - ugh better way though?
      !this.event.is_upgrade // not an upgrade
    ) {
      // ...in which case we need to choose a sends offer that covers the
      // number of guests just sent to plus those being added
      const numAdded = this.calc.numGuestsBeingAdded ? this.calc.numGuestsBeingAdded : 0;
      const numSent = this.calc.numGuestsAlreadySent ? this.calc.numGuestsAlreadySent : 0;
      const numSendsNeeded = numAdded + numSent;
      for (let idx = 0; idx < allowedSendsOfferKeys.length; idx++) {
        const sendsOffer = availableSendsOffers.find(
          (offer) => offer.offerKey === allowedSendsOfferKeys[idx]
        );
        if (sendsOffer && sendsOffer.virtualQuantity >= numSendsNeeded) return sendsOffer;
      }
    }

    return availableSendsOffers.find((offer) => offer.offerKey === allowedSendsOfferKeys[0]);
  };

  @computed
  get offersToCharge() {
    const offers = [];
    for (const key of Object.keys(this.calc.selectedOffers)) {
      const quantity = this.calc.selectedOffers[key]; // If the value is 0, this offer was removed/downgraded.
      // only add the current offer if it's not removed / downgraded.
      if (quantity) {
        const offer = this.calc.availableOffers.find((offer) => offer.offerKey === key);
        if (!offer) {
          // This is for users that have events old offers that's no longer available (like premium pass)
          this.selectSmallerAvailableOffer();
        } else {
          offers.push({offer, amount: offer.amount, quantity});
        }
      }
    }
    return offers;
  }

  @computed
  get hasSelectedOffer() {
    return this.offersToCharge.length > 0;
  }

  onEventCreate = () => {
    // don't update unless we have a need to. this avoids excessive
    // transaction conflicts on the server
    // because this call also saves the event
    this.hasSelectedOffer && this.updateSelectedOffers();
  };

  fireUserSawPricingTiersEvent = () => {
    window.dataLayer.push({
      event: 'userSawPricingTiers',
      event_id: this.hasEvent ? this.event.id : '',
    });
  };
}
